import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import { brandLine, sponsorCard, signupBox, CURRENCY_COUNTRY } from '../sponsorHooks';
const CURRENCIES = ['NGN', 'USD', 'GBP', 'CAD', 'EUR', 'AED'];
const CURRENCY_SYMBOLS = {
  NGN: '₦',
  USD: '$',
  GBP: '£',
  CAD: 'C$',
  EUR: '€',
  AED: 'د.إ'
};
const FREQUENCY_MULTIPLIERS = {
  yearly: 1,
  monthly: 12,
  biweekly: 26,
  weekly: 52,
  daily: 260
  // hourly handled separately since it depends on hoursPerWeek
};
function formatMoney(amount, currency) {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return symbol + new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
}
export default class SalaryConverterPage extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.fromCurrency = 'NGN';
    this.toCurrency = 'USD';
    this.amount = 50000;
    this.frequency = 'monthly';
    this.hoursPerWeek = 37.5;
    this.exchangeRates = {
      USD: 1580,
      CAD: 1130,
      GBP: 1950,
      EUR: 1680,
      AED: 430,
      NGN: 1
    };
    this.ratesLoaded = false;
    this.loadExchangeRates();
    this.results = null;
    this.error = null;
  }
  async loadExchangeRates() {
    try {
      const response = await fetch('https://api.exchangerate-api.com/v4/latest/NGN');
      if (!response.ok) throw new Error('rate fetch failed');
      const data = await response.json();
      this.exchangeRates = {
        USD: Math.round(1 / data.rates.USD * 100) / 100,
        CAD: Math.round(1 / data.rates.CAD * 100) / 100,
        GBP: Math.round(1 / data.rates.GBP * 100) / 100,
        EUR: Math.round(1 / data.rates.EUR * 100) / 100,
        AED: Math.round(1 / data.rates.AED * 100) / 100,
        NGN: 1
      };
    } catch (e) {
      // fallback rates already set in oninit
    }
    this.ratesLoaded = true;
    m.redraw();
  }
  exchangeRateBetween(from, to) {
    if (from === to) return 1;
    const fromToNGN = from === 'NGN' ? 1 : this.exchangeRates[from];
    const toToNGN = to === 'NGN' ? 1 : this.exchangeRates[to];
    return fromToNGN / toToNGN;
  }
  convert() {
    const amount = parseFloat(this.amount) || 0;
    const hoursPerWeek = parseFloat(this.hoursPerWeek) || 37.5;
    if (amount <= 0) {
      this.error = 'Please enter a valid salary amount.';
      return;
    }
    this.error = null;
    const multiplier = this.frequency === 'hourly' ? hoursPerWeek * 52 : FREQUENCY_MULTIPLIERS[this.frequency] || 1;
    const yearlyAmount = amount * multiplier;
    const rate = this.exchangeRateBetween(this.fromCurrency, this.toCurrency);
    const convertedYearly = yearlyAmount * rate;
    this.results = {
      fromCurrency: this.fromCurrency,
      toCurrency: this.toCurrency,
      originalAmount: amount,
      frequency: this.frequency,
      hoursPerWeek,
      rate,
      yearly: convertedYearly,
      monthly: convertedYearly / 12,
      biweekly: convertedYearly / 26,
      weekly: convertedYearly / 52,
      daily: convertedYearly / 260,
      hourly: convertedYearly / (hoursPerWeek * 52)
    };
    this.saveConversionSilently();
  }

  // No Save button or history UI for this calculator by design — this just
  // silently records the conversion for logged-in users in the background,
  // matching what the original standalone tool did on every convert click.
  async saveConversionSilently() {
    if (!app.session.user || !this.results) return;
    try {
      await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/voucha-calculators/salary/save',
        body: {
          from_currency: this.results.fromCurrency,
          to_currency: this.results.toCurrency,
          amount: this.results.originalAmount,
          converted_amount: this.results.yearly,
          exchange_rate: this.results.rate
        }
      });
    } catch (e) {
      // Silent by design — a failed background save shouldn't interrupt
      // the user's experience of getting their conversion result.
    }
  }
  reset() {
    this.fromCurrency = 'NGN';
    this.toCurrency = 'USD';
    this.amount = 50000;
    this.frequency = 'monthly';
    this.hoursPerWeek = 37.5;
    this.results = null;
    this.error = null;
  }
  view() {
    return m("div", {
      className: "VouchaCalculator container"
    }, m("div", {
      className: "VouchaCalculator-header"
    }, m("h1", null, "Salary Converter"), m("p", {
      className: "helpText"
    }, "Convert your salary between Naira and major foreign currencies, across any pay frequency.")), m("div", {
      className: "VouchaCalculator-form"
    }, m("div", {
      className: "Form-group"
    }, m("label", null, "From Currency"), m("select", {
      value: this.fromCurrency,
      onchange: e => this.fromCurrency = e.target.value
    }, CURRENCIES.map(c => m("option", {
      value: c
    }, c)))), m("div", {
      className: "Form-group"
    }, m("label", null, "To Currency"), m("select", {
      value: this.toCurrency,
      onchange: e => this.toCurrency = e.target.value
    }, CURRENCIES.map(c => m("option", {
      value: c
    }, c)))), m("div", {
      className: "Form-group"
    }, m("label", null, "Salary Amount"), m("input", {
      type: "number",
      value: this.amount,
      oninput: e => this.amount = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Pay Frequency"), m("select", {
      value: this.frequency,
      onchange: e => this.frequency = e.target.value
    }, m("option", {
      value: "yearly"
    }, "Yearly"), m("option", {
      value: "monthly"
    }, "Monthly"), m("option", {
      value: "biweekly"
    }, "Biweekly"), m("option", {
      value: "weekly"
    }, "Weekly"), m("option", {
      value: "daily"
    }, "Daily"), m("option", {
      value: "hourly"
    }, "Hourly"))), m("div", {
      className: "Form-group"
    }, m("label", null, "Work Hours per Week"), m("input", {
      type: "number",
      step: "0.5",
      value: this.hoursPerWeek,
      oninput: e => this.hoursPerWeek = e.target.value
    })), m("div", {
      className: "Form-group"
    }, m("label", null, "Exchange Rate"), m("input", {
      type: "text",
      disabled: true,
      value: this.ratesLoaded ? `1 ${this.fromCurrency} = ${this.exchangeRateBetween(this.fromCurrency, this.toCurrency).toFixed(4)} ${this.toCurrency}` : 'Loading…'
    })), this.error && m("div", {
      className: "VouchaCalculator-error"
    }, this.error), m("div", {
      className: "VouchaCalculator-formActions"
    }, Button.component({
      className: 'Button Button--primary',
      onclick: () => this.convert()
    }, 'Convert'), Button.component({
      className: 'Button',
      onclick: () => this.reset()
    }, 'Reset'))), this.results && sponsorCard('salary', this.sponsorCountry(this.results)), this.results && this.renderResults(), m("div", {
      className: "VouchaCalculator-relatedTools"
    }, m("h2", null, "Related tools"), m(Link, {
      href: "/tax-calculator"
    }, "Income Tax Calculator"), m(Link, {
      href: "/business-tax"
    }, "Business Tax Calculator")), signupBox('calculator-salary'));
  }

  /** The foreign (non-Naira) side of the conversion decides the sponsor's country */
  sponsorCountry(r) {
    const foreign = r.toCurrency !== 'NGN' ? r.toCurrency : r.fromCurrency;
    return CURRENCY_COUNTRY[foreign] || null;
  }
  renderResults() {
    const r = this.results;
    return m("div", {
      className: "VouchaCalculator-results"
    }, m("h2", null, formatMoney(r.originalAmount, r.fromCurrency), " ", r.frequency, " is how much in ", r.toCurrency, "?"), brandLine('salary', this.sponsorCountry(r), r.fromCurrency + ' to ' + r.toCurrency + ' results'), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Work Hours per Week"), m("span", null, r.hoursPerWeek)), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Yearly Salary"), m("span", null, formatMoney(r.yearly, r.toCurrency))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Monthly Salary"), m("span", null, formatMoney(r.monthly, r.toCurrency))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Biweekly Salary"), m("span", null, formatMoney(r.biweekly, r.toCurrency))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Weekly Salary"), m("span", null, formatMoney(r.weekly, r.toCurrency))), m("div", {
      className: "VouchaCalculator-resultRow"
    }, m("span", null, "Daily Salary"), m("span", null, formatMoney(r.daily, r.toCurrency))), m("div", {
      className: "VouchaCalculator-resultRow VouchaCalculator-resultRow--net"
    }, m("span", null, "Hourly Salary"), m("span", null, formatMoney(r.hourly, r.toCurrency))));
  }
}
flarum.reg.add('voucha-calculators', 'forum/components/SalaryConverterPage', SalaryConverterPage);